import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';

const Dropdown = ({ trigger, children, className = '', align = 'right' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const dropdownRef = useRef(null);
  const triggerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current && !dropdownRef.current.contains(event.target) &&
        triggerRef.current && !triggerRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    const handleScroll = () => {
      if (isOpen) setIsOpen(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('scroll', handleScroll, true);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [isOpen]);

  const handleToggle = (e) => {
    e.stopPropagation();
    if (!isOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const menuWidth = 192; // 12rem / w-48
      
      let leftPos = align === 'right' ? rect.right - menuWidth : rect.left;
      if (leftPos < 8) leftPos = 8;
      if (leftPos + menuWidth > window.innerWidth - 8) {
        leftPos = window.innerWidth - menuWidth - 8;
      }

      setPosition({
        top: rect.bottom + 6,
        left: leftPos,
      });
    }
    setIsOpen(prev => !prev);
  };

  return (
    <div className={`relative inline-block text-left ${className}`}>
      <div ref={triggerRef} onClick={handleToggle} className="cursor-pointer">
        {trigger}
      </div>
      
      {isOpen && createPortal(
        <div 
          ref={dropdownRef}
          className="fixed w-48 rounded-xl shadow-xl z-[9999] bg-white dark:bg-[#1e293b] border border-slate-200/90 dark:border-slate-700 py-1.5 animate-fadeIn"
          style={{
            top: `${position.top}px`,
            left: `${position.left}px`,
          }}
          onClick={(e) => {
            e.stopPropagation();
            setIsOpen(false);
          }}
        >
          {children}
        </div>,
        document.body
      )}
    </div>
  );
};

const DropdownItem = ({ onClick, children, className = '', disabled = false, icon = null }) => {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`
        w-full text-left px-4 py-2 text-xs font-medium flex items-center gap-2.5
        transition-colors duration-150
        ${disabled 
          ? 'opacity-40 cursor-not-allowed text-slate-400' 
          : 'text-slate-700 dark:text-slate-200 hover:bg-emerald-50 hover:text-emerald-700 dark:hover:bg-slate-700/60 dark:hover:text-emerald-400 cursor-pointer'}
        ${className}
      `}
      role="menuitem"
    >
      {icon && <span className="text-slate-400 shrink-0">{icon}</span>}
      {children}
    </button>
  );
};

export { Dropdown, DropdownItem };